<script setup>
// core
import {computed, ref} from "vue"
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'

// data
import { LEVELS } from "@/data/notifications"

// icons
import InfoIcon from "@/components/Notifications/icons/info.vue"
import ErrorIcon from "@/components/Notifications/icons/error.vue"
import WarningIcon from "@/components/Notifications/icons/warning.vue"
import SuccessIcon from "@/components/Notifications/icons/success.vue"

const { locale } = useI18n()
const currentLanguage = ref(locale.value)

const props = defineProps({
    item: {
        type: Object,
        required: true,
        default: () => { }
    },
    descriptionLoadingItem: {
        type: [Number, null],
        required: true,
        default: null
    }
})
defineEmits(['get-description'])
const isCollapse = ref(false);

const Icons = {
    [LEVELS.INFO]: InfoIcon,
    [LEVELS.WARNING]: WarningIcon,
    [LEVELS.ERROR]: ErrorIcon,
    [LEVELS.SUCCESS]: SuccessIcon,
}

const localFormatted = computed(() =>
    DateTime
        .fromFormat(props.item.created_at, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
        .toLocal()
        .toFormat('yyyy-MM-dd HH:mm:ss')
)
</script>

<template>
    <div class="notification-element" @click="$emit('get-description', item.id)">
        <div class="notification-element__icon">
            <component :is="Icons[item.level]" />
        </div>
        <div class="notification-element__content">
            <div class="notification-element__base" @click="isCollapse = !isCollapse">
                <h6 class="notification-element__title">
                    {{ item.title[currentLanguage] }}
                </h6>
                <div v-if="isCollapse" class="notification-element__description">
                    <div v-if="descriptionLoadingItem === item.id" class="notification-element__description-loading">
                        <i class="spinner"></i>
                    </div>
                    <div v-else v-html="item.text?.[currentLanguage]"></div>
                </div>
                <div class="notification-element__secondary">
                    <div class="notification-element__secondary-date">{{ localFormatted }}</div>
                    <div class="notification-element__secondary-more">
                        {{ isCollapse ? $t('notifications.hide_btn') : $t('notifications.show_more_btn') }}
                        <i class="bi" :class="{ 'bi-chevron-up': isCollapse, 'bi-chevron-down': !isCollapse }"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.notification-element {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    cursor: pointer;

    &__content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
    }

    &__base {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &__title {
        color: #212529;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        word-break: break-word;
    }

    &__description {
        min-height: 25px;

        &-loading {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    &__secondary {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &-date {
            color: #656565;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
        }

        &-more {
            cursor: pointer;
            color: #656565;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }
    }
}
</style>
